import type { FC, FormEvent, ReactNode } from 'react'
import type { LoginRole } from '@/types'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { api, errorMessage } from '@/lib/api-client'
import { useToast } from '@/components/generals/toast'
import { Field, SelectInput, TextArea, TextInput } from '@/components/generals/form'

import {
    ArrowRight,
    Check,
    GraduationCap,
    LoaderCircle,
    MessagesSquare,
    Send,
    Users
} from 'lucide-react'

import { INQUIRY_INTERESTS, INQUIRY_YEARS } from '@/constants/campus'

interface InquirySectionProps {
    onOpenLogin: (role: LoginRole, signUp?: boolean) => void
}

interface InquiryForm {
    name: string
    email: string
    phone: string
    rollOrDepartment: string
    yearLabel: string
    interests: Array<string>
    message: string
    website: string
}

type InquiryErrors = Partial<Record<keyof InquiryForm, string>>

const EMPTY: InquiryForm = { name: '', email: '', phone: '', rollOrDepartment: '', yearLabel: '', interests: [], message: '', website: '' }

const validate = (form: InquiryForm): InquiryErrors => {
    const errors: InquiryErrors = {}
    const digits = form.phone.replace(/[^\d]/g, '')

    if (form.name.trim().length < 2) errors.name = 'Enter your full name.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errors.email = 'Enter a valid college email.'
    if (!/^[6-9]\d{9}$/.test(digits)) errors.phone = 'Enter a 10-digit mobile number.'
    if (form.rollOrDepartment.trim().length < 2) errors.rollOrDepartment = 'Enter your roll number or department.'
    if (!form.yearLabel) errors.yearLabel = 'Choose your B.Tech year.'
    if (!form.interests.length) errors.interests = 'Pick at least one option.'

    return errors
}

export const InquirySection: FC<InquirySectionProps> = ({ onOpenLogin }): ReactNode => {
    const { toast } = useToast()
    const [form, setForm] = useState<InquiryForm>(EMPTY)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)

    const update = <K extends keyof InquiryForm>(key: K, value: InquiryForm[K]) => {
        setForm(current => ({ ...current, [key]: value }))
        setErrors(current => ({ ...current, [key]: undefined }))
    }

    const toggleInterest = (interest: string) => {
        update('interests', form.interests.includes(interest) ? form.interests.filter(item => item !== interest) : [...form.interests, interest])
    }

    const submit = async (event: FormEvent) => {
        event.preventDefault()

        const problems = validate(form)

        setErrors(problems)

        if (Object.keys(problems).length) return

        setSending(true)

        try {
            await api.submitInquiry({ ...form, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), rollOrDepartment: form.rollOrDepartment.trim(), message: form.message.trim() })

            toast(`Thanks, ${form.name.trim().split(' ')[0]}. Your inquiry is in.`, { description: 'The GKV FET Student Pioneer Team will get back to you by email.' })
            setForm(EMPTY)
            setSent(true)
        }
        catch (error) {
            toast('Your inquiry was not sent', { description: errorMessage(error), tone: 'error' })
        }
        finally {
            setSending(false)
        }
    }

    return (
        <section id='contact' aria-labelledby='contact-title' className='bg-white px-4 pb-24 sm:px-6'>
            <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className='mx-auto grid max-w-6xl gap-10 rounded-3xl border border-slate-200 bg-alabaster p-6 shadow-xl sm:p-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]'
            >
                <div className='flex flex-col'>
                    <span className='inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-600 ring-1 ring-indigo-100'>
                        <MessagesSquare className='size-3.5' />
                        Book a campus pass · Need help?
                    </span>

                    <h2 id='contact-title' className='mt-5 text-balance text-3xl font-extrabold leading-[1.12] tracking-tight text-ink sm:text-4xl'>
                        Have Questions or Want a Guided Campus Demo?
                    </h2>

                    <p className='mt-4 text-[15px] leading-relaxed text-slate-600'>
                        Reach out to the GKV FET Student Pioneer Team or T&P Cell for bulk batch onboarding, scholarship queries, or club collaboration.
                    </p>

                    <ul className='mt-8 space-y-4'>
                        {[
                            { icon: GraduationCap, title: "Batch onboarding", text: "Register a whole class or branch in one go" },
                            { icon: Users, title: "Club collaboration", text: "Co-host events with the Gurukul Engineers Club" },
                            { icon: Send, title: "Placement drives", text: "Recruiters and T&P coordination" }
                        ].map(item => (
                            <li key={item.title} className='flex items-start gap-3'>
                                <span className='grid size-10 shrink-0 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200'>
                                    <item.icon className='size-5' />
                                </span>

                                <span>
                                    <span className='block text-sm font-bold text-ink'>{item.title}</span>
                                    <span className='block text-sm text-slate-500'>{item.text}</span>
                                </span>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-8 rounded-2xl border border-slate-200 bg-white p-5 lg:mt-auto'>
                        <p className='text-sm font-semibold text-ink'>Already have an account?</p>
                        <p className='mt-1 text-sm text-slate-500'>Check your pass status, or open the T&P desk.</p>

                        <button
                            type='button'
                            onClick={() => onOpenLogin('st')}
                            className='mt-4 inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800'
                        >
                            Sign In
                            <ArrowRight className='size-4' />
                        </button>
                    </div>
                </div>

                <form noValidate onSubmit={submit} className='rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7'>
                    {sent && (
                        <div role='status' className='mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800'>
                            <Check className='mt-0.5 size-4 shrink-0' strokeWidth={3} />
                            Your inquiry reached the team. You can send another one any time.
                        </div>
                    )}

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <Field label='Full Name' htmlFor='inquiry-name' required error={errors.name}>
                            <TextInput id='inquiry-name' value={form.name} onChange={event => update('name', event.target.value)} autoComplete='name' placeholder='Your full name' maxLength={100} invalid={Boolean(errors.name)} describedBy='inquiry-name-error' />
                        </Field>

                        <Field label='College Email' htmlFor='inquiry-email' required error={errors.email}>
                            <TextInput id='inquiry-email' type='email' value={form.email} onChange={event => update('email', event.target.value)} autoComplete='email' placeholder='you@gkv.ac.in' maxLength={190} invalid={Boolean(errors.email)} describedBy='inquiry-email-error' />
                        </Field>

                        <Field label='Phone Number' htmlFor='inquiry-phone' required error={errors.phone}>
                            <div className='flex'>
                                <span className='inline-flex items-center rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-600'>+91</span>
                                <TextInput id='inquiry-phone' type='tel' value={form.phone} onChange={event => update('phone', event.target.value)} autoComplete='tel-national' inputMode='numeric' placeholder='98765 43210' maxLength={14} className='rounded-l-none' invalid={Boolean(errors.phone)} describedBy='inquiry-phone-error' />
                            </div>
                        </Field>

                        <Field label='Roll No / Department' htmlFor='inquiry-roll' required error={errors.rollOrDepartment}>
                            <TextInput id='inquiry-roll' value={form.rollOrDepartment} onChange={event => update('rollOrDepartment', event.target.value)} placeholder='23FET1042 or CSE Dept.' maxLength={80} invalid={Boolean(errors.rollOrDepartment)} describedBy='inquiry-roll-error' />
                        </Field>

                        <Field label='B.Tech Year' htmlFor='inquiry-year' required error={errors.yearLabel} className='sm:col-span-2'>
                            <SelectInput id='inquiry-year' value={form.yearLabel} onChange={event => update('yearLabel', event.target.value)} invalid={Boolean(errors.yearLabel)} describedBy='inquiry-year-error'>
                                <option value='' disabled>Choose your year</option>
                                {INQUIRY_YEARS.map(year => <option key={year} value={year}>{year}</option>)}
                            </SelectInput>
                        </Field>

                        <fieldset className='sm:col-span-2'>
                            <legend className='mb-2 text-[13px] font-semibold text-slate-800'>
                                Interested In <span aria-hidden='true' className='text-rose-500'>*</span>
                            </legend>

                            <div className='flex flex-wrap gap-2'>
                                {INQUIRY_INTERESTS.map(interest => {
                                    const selected = form.interests.includes(interest)

                                    return (
                                        <motion.button
                                            key={interest}
                                            type='button'
                                            aria-pressed={selected}
                                            onClick={() => toggleInterest(interest)}
                                            whileTap={{ scale: 0.96 }}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors',
                                                selected ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-ink'
                                            )}
                                        >
                                            {selected && <Check className='size-3.5' strokeWidth={3} />}
                                            {interest}
                                        </motion.button>
                                    )
                                })}
                            </div>

                            {errors.interests && <p className='mt-1.5 text-xs font-medium text-rose-600'>{errors.interests}</p>}
                        </fieldset>

                        <Field label='Anything else? (optional)' htmlFor='inquiry-message' className='sm:col-span-2'>
                            <TextArea id='inquiry-message' value={form.message} onChange={event => update('message', event.target.value)} placeholder='Batch size, preferred dates, scholarship details...' maxLength={1000} />
                        </Field>

                        <div aria-hidden='true' className='absolute -left-[9999px] size-px overflow-hidden'>
                            <label htmlFor='inquiry-website'>Leave this field empty</label>
                            <input id='inquiry-website' tabIndex={-1} autoComplete='off' value={form.website} onChange={event => update('website', event.target.value)} />
                        </div>
                    </div>

                    <motion.button
                        type='submit'
                        disabled={sending}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        className='mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-crimson px-6 py-3.5 text-sm font-semibold text-white shadow-[0_14px_36px_-12px_rgba(217,45,32,0.8)] transition-colors hover:bg-crimson-dark disabled:opacity-60'
                    >
                        {sending ? <LoaderCircle className='size-4 animate-spin' /> : <Send className='size-4' />}
                        {sending ? 'Sending your inquiry' : 'Submit Inquiry'}
                    </motion.button>

                    <p className='mt-3 text-center text-xs text-slate-500'>
                        We reply by email. Your details are only used to answer this inquiry.
                    </p>
                </form>
            </motion.div>
        </section>
    )
}
